import React, { useState, useEffect } from 'react';

interface FeedbackDisplayProps {
  hasGuessed: boolean;
  actualYear: number | null;
  guessYear: number | null;
  pointsGained: number;
}

const FeedbackDisplay: React.FC<FeedbackDisplayProps> = ({
  hasGuessed,
  actualYear,
  guessYear,
  pointsGained,
}) => {
  const [animatedPoints, setAnimatedPoints] = useState(0);

  useEffect(() => {
    if (hasGuessed && pointsGained > 0) {
      let start = 0;
      const increment = Math.max(1, Math.floor(pointsGained / 15)); // Animate in about 15 steps
      const interval = setInterval(() => {
        start += increment;
        if (start >= pointsGained) {
          setAnimatedPoints(pointsGained);
          clearInterval(interval);
        } else {
          setAnimatedPoints(start);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [hasGuessed, pointsGained]);

  if (!hasGuessed || !actualYear || !guessYear) {
    return null;
  }

  // Calculate difference and determine feedback message
  const yearDiff = Math.abs(guessYear - actualYear);
  let yearsOffMessage = '';
  let emoji = '';

  if (yearDiff === 0) {
    yearsOffMessage = 'You got it!';
    emoji = '🎯';
  } else if (yearDiff === 1) {
    yearsOffMessage = 'Only 1 year off!';
    emoji = '🤏';
  } else if (yearDiff <= 5) {
    yearsOffMessage = `${yearDiff} years off!`;
    emoji = '🤏';
  } else if (yearDiff <= 20) {
    yearsOffMessage = `${yearDiff} years off!`;
    emoji = '';
  } else {
    yearsOffMessage = `${yearDiff} years off!`;
    emoji = '😬';
  }

  // Convert years off to emoji if 10 or less
  const yearsOffDisplay =
    yearDiff === 0
      ? '' // Don't show any number for exact match
      : yearDiff <= 10
        ? yearDiff === 10
          ? '🔟'
          : `${yearDiff}️⃣`
        : yearDiff.toString();

  return (
    <div className="flex flex-col items-center text-center mt-[-8px] md:mt-[65px] mb-8 md:mb-[29px]">
      {/* Years off message */}
      <div className="text-white text-[20px] font-bold my-1 whitespace-nowrap">
        Answer: <span className="text-[#C6A4FF]">{actualYear}</span>
      </div>

      {/* Actual year message and emoji */}
      <div className="flex items-center justify-center gap-1 mb-3">
        <div className="text-white/70 text-xs uppercase font-bold">
          {yearsOffMessage.split(yearDiff.toString())[0]}
          <span className={yearDiff <= 10 ? 'text-white' : 'text-white/70'}>
            {yearsOffDisplay}
          </span>
          {yearsOffMessage.split(yearDiff.toString())[1]}
        </div>
        {emoji && <span className="text-xs">{emoji}</span>}
      </div>

      {/* Points gained section */}
      <div className="text-[#FFC300] text-[16px] font-bold uppercase">
        +{animatedPoints} Pts
      </div>
    </div>
  );
};

export default FeedbackDisplay;
